import { useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { api } from "../lib/api/client";
import type { Branding } from "../lib/api/types";
import { brandCssVars, deriveBrandPalette, NEUTRAL_BRAND } from "../lib/theme";
import { BrandingProvider } from "../lib/state/brandingContext";
import { useCatalogueRouteMode, useTenantSlug } from "../lib/tenant";

// One-time global stylesheet. Inline styles can't express hover/focus/active, so the few
// stateful rules (buttons, tiles, inputs) live here, keyed on class names. Colours resolve
// from the brand CSS variables set on the layout wrapper.
const GLOBAL_CSS = `
*, *::before, *::after { box-sizing: border-box; }
body { margin: 0; }

.rg-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border: 1px solid transparent; border-radius: var(--radius-pill);
  font-family: inherit; font-weight: 600; font-size: 14px; line-height: 1;
  padding: 11px 18px; cursor: pointer; white-space: nowrap; text-decoration: none;
  transition: background 120ms ease, box-shadow 120ms ease, border-color 120ms ease, color 120ms ease;
}
.rg-btn > span { display: inline-flex; }
.rg-btn--sm { padding: 8px 14px; font-size: 13px; }
.rg-btn--block { width: 100%; }
.rg-btn:disabled, .rg-btn[aria-disabled="true"] { opacity: 0.5; cursor: default; pointer-events: none; }
.rg-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--ring); }

/* Touch devices (the visualizer's dominant audience): give primary controls a 44px min hit height
   (iOS HIG / Material) so the first-screen CTAs — Browse files, Find my home, Make it photorealistic,
   Get a quote — aren't mis-tapped. Desktop/mouse layout is unchanged. */
@media (pointer: coarse) {
  .rg-btn { min-height: 44px; }
  .rg-btn--sm { min-height: 40px; }
  .rg-input { min-height: 44px; }
}

.rg-btn--primary { background: var(--brand); color: var(--on-brand); }
.rg-btn--primary:hover { background: var(--brand-hover); }
.rg-btn--primary:active { background: var(--brand-active); }

.rg-btn--secondary { background: var(--surface); color: var(--text); border-color: var(--border-strong); }
.rg-btn--secondary:hover { background: var(--brand-tint); border-color: var(--brand); color: var(--brand); }

.rg-btn--ghost { background: transparent; color: var(--brand); }
.rg-btn--ghost:hover { background: var(--brand-tint); }

.rg-tile {
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}
.rg-tile:hover { border-color: var(--brand); box-shadow: var(--shadow-sm); }
.rg-tile:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--ring); }

/* Select-screen option cards: subtle hover lift (no transform → no layout shift while typing). */
.rg-option { transition: border-color 140ms ease, box-shadow 140ms ease; }
.rg-option:hover { border-color: var(--brand); box-shadow: var(--shadow-md); }
.rg-option:focus-within { border-color: var(--brand); box-shadow: var(--shadow-md); }

/* Select-screen option grid: three equal columns, collapsing straight to one on tablet/phone — never a
   lopsided 2+1 (which would leave the third card a different height than its row-mates). */
.rg-select-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; align-items: stretch; max-width: 900px; margin: 0 auto; }
@media (max-width: 760px) { .rg-select-grid { grid-template-columns: 1fr; max-width: 420px; } }

/* "Try Styles" layout: editor + picker side-by-side on desktop; on mobile it collapses to one column
   (display:contents flattens the two column wrappers) with the live preview kept sticky at the top so the
   homeowner watches the door update while scrolling the style picker. */
.rg-visualize-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr); gap: 16px; align-items: start; }
.rg-visualize-col { display: flex; flex-direction: column; gap: 12px; }
.rg-quad-wrap { width: 100%; }
.rg-quad-img { width: 100%; display: block; border-radius: 8px; }
@media (max-width: 860px) {
  .rg-visualize-grid { grid-template-columns: 1fr; }
  .rg-visualize-col { display: contents; }
  .rg-visualize-preview { position: sticky; top: 8px; z-index: 5; }
  /* Cap the preview so a tall/portrait photo doesn't fill the screen; the wrapper hugs the scaled image
     so the draggable overlay stays pixel-aligned with it. 36vh (not taller): the sticky card's height is
     the single scarcest budget on a phone — everything below it lives in what's left of the viewport. */
  .rg-quad-wrap { width: fit-content; max-width: 100%; margin: 0 auto; }
  .rg-quad-img { width: auto; max-width: 100%; max-height: 36vh; height: auto; }
}

/* App header: wrap the step nav under the brand on small screens instead of crowding it onto one line. */
.rg-appbar { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; gap: 12px; padding: 12px 20px; }
.rg-appbar-nav { margin-left: auto; display: flex; gap: 20px; font-size: 14px; }
@media (max-width: 560px) {
  .rg-appbar { flex-wrap: wrap; row-gap: 10px; }
  .rg-appbar-nav { margin-left: 0; width: 100%; gap: 14px; justify-content: space-between; font-size: 13px; }
}

.rg-input {
  width: 100%; padding: 10px 12px; font-family: inherit; font-size: 14px;
  border: 1px solid var(--border-strong); border-radius: var(--radius-sm);
  background: var(--surface); color: var(--text);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rg-input:focus { outline: none; border-color: var(--brand); box-shadow: 0 0 0 3px var(--ring); }

.rg-spin { display: inline-block; border-radius: 50%; box-sizing: border-box; animation: rg-spin 0.8s linear infinite; }
@keyframes rg-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .rg-spin { animation-duration: 2.4s; } }

/* ---- Try Styles mobile UX (base rules first, then their media overrides — order matters at equal
   specificity, so this section is self-contained and appended last). ---- */

/* Accordion section body: on desktop a capped inner scroller keeps the right rail compact; on mobile
   (≤860px, no rule here) the body flows at natural height so the PAGE is the only scroll context —
   nested scrolling inside page scroll is the worst touch pattern the old inline maxHeight created. */
.rg-section-body { padding: 2px 14px 14px; }
@media (min-width: 861px) {
  .rg-section-body { max-height: 360px; overflow-y: auto; overscroll-behavior: contain; }
}

/* Collapsed-section summary: desktop shows the wide preview band; mobile swaps it for a compact
   inline thumb in the header row, so every collapsed header is a uniform ~46px and toggling sections
   no longer jumps the page by a band's height. Both stay mounted — CSS only toggles visibility. */
.rg-section-head { min-height: 44px; justify-content: center; }
.rg-acc-band { display: block; }
.rg-acc-thumb { display: none; }

/* Preview-card chrome knobs: inline styles read these vars, media queries retune them. */
.rg-mode-row { margin-bottom: 12px; }

/* On-photo media buttons (fullscreen expand): compact on desktop, full 44px touch target on coarse. */
.rg-media-btn { width: 34px; height: 34px; }

/* Invisible hit-slop for the QuadEditor drag handles: pseudo-elements enlarge the hit-test area
   without touching the visual size or any positioning math (the parent stays the event target). */
.rg-corner-handle::after, .rg-sub-handle::after { content: ""; position: absolute; inset: -8px; border-radius: 50%; }

/* Mobile-only sticky quote bar (price + CTA). Hidden on desktop where the in-flow card is visible. */
.rg-quote-bar { display: none; }

/* Fullscreen editing surface: the photo may use (nearly) the whole viewport — this outranks both
   the desktop default and the mobile 36vh cap by specificity. Height reserves room for the bottom
   chrome (hint + Done); width leaves a hairline gutter so corner handles at the edge stay grabbable. */
.rg-fs-editor .rg-quad-img { width: auto; height: auto; max-width: calc(100vw - 12px); max-height: calc(100vh - 150px); max-height: calc(100dvh - 150px); }

@media (max-width: 860px) {
  /* One 12px beat between stacked cards — the 16px column gap was the largest spacing on the
     smallest screen. */
  .rg-visualize-grid { gap: 12px; padding-bottom: calc(84px + env(safe-area-inset-bottom)); }
  /* Slim the pinned card: tighter padding via the var the inline style reads. */
  .rg-visualize-preview { --rg-preview-pad: 10px; }
  .rg-mode-row { margin-bottom: 8px; }
  /* Marketing-only copy is hidden statically per-breakpoint (never toggles with render state, so the
     anti-jitter guarantees hold); actionable variants opt out of this class at the call site. */
  .rg-hide-m { display: none; }
  /* Collapsed headers: band off, inline thumb on. */
  .rg-acc-band { display: none; }
  .rg-acc-thumb { display: block; }
  /* The style picker is the core loop — put it right under the pinned photo; doors/geometry are
     setup admin most 1-door sessions never touch. (The columns are display:contents here, so every
     card is a grid item and the order property reorders without DOM moves.) */
  .rg-doors-card { order: 6; }
  .rg-geom-panel { order: 7; }
  /* The in-flow CTA duplicates the sticky bar on mobile. */
  .rg-btn.rg-quote-inflow { display: none; }
  .rg-quote-bar {
    display: flex; align-items: center; gap: 12px;
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 20;
    background: var(--surface); border-top: 1px solid var(--border);
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
    box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.08);
  }
}

@media (pointer: coarse) {
  .rg-section-head { min-height: 48px; }
  .rg-media-btn { width: 44px; height: 44px; }
  .rg-corner-handle::after { inset: -10px; }
  .rg-sub-handle::after { inset: -14px; }
}

/* Scroll-linked preview collapse (mobile): as the customer scrolls into the picker, the pinned
   photo eases 36vh -> 24vh over the first 260px of scroll — more room for options mid-browse, the
   full hero back the moment they return to the top. The height is a pure function of scroll
   position (no listeners, no feedback loop). Progressive enhancement: browsers without
   scroll-driven animations keep the static 36vh cap; reduced-motion users are excluded; scoped to
   the preview card so the fullscreen editor's img keeps its own viewport-sized cap. Longhands
   only — the animation shorthand RESETS animation-timeline. */
@supports (animation-timeline: scroll(root)) {
  @media (max-width: 860px) and (prefers-reduced-motion: no-preference) {
    .rg-visualize-preview .rg-quad-img {
      animation-name: rg-preview-collapse;
      animation-duration: 1ms; /* ignored by scroll timelines; keeps older parsers from dropping the animation */
      animation-timing-function: linear;
      animation-fill-mode: both;
      animation-timeline: scroll(root block);
      animation-range: 0px 260px;
    }
    /* The collapsing pinned card changes layout above the fold on every scroll frame — keep the
       browser's scroll anchoring from fighting it. */
    .rg-visualize-grid { overflow-anchor: none; }
  }
}
/* Collapse floor: never below the photo's WIDTH-FILLING height — shrinking a landscape photo past
   full card width buys no stack height back (the card keeps its width) and just leaves dead side
   gutters. 62px = main padding (20×2) + mobile card padding (10×2) + card border (1×2). The
   --rg-photo-ar var (width:height, set by QuadEditor at image load; 1.5 until known) makes the
   width-filling height computable in pure CSS. Portrait/square photos (ratio ≲ 1.07) still collapse
   the full 36vh → 24vh — they're the ones that could otherwise fill the screen. */
@keyframes rg-preview-collapse {
  from { max-height: 36vh; }
  to { max-height: max(24vh, calc((100vw - 62px) / var(--rg-photo-ar, 1.5))); }
}

/* Short TOUCH viewports — landscape phones of ANY width, 812×375 up to Pro-Max 932×430 (which are
   >860px wide and would otherwise get uncapped desktop sizing on a 430px-tall screen). pointer:
   coarse keeps short desktop browser windows out. Height-critical rules live here, width-agnostic;
   these blocks sit LAST in the sheet so they win every equal-specificity rule above. */
@media (max-height: 500px) and (pointer: coarse) {
  .rg-appbar { padding: 6px 20px; }
  /* Height-cap the photo; wrap must hug it (fit-content) so the QuadEditor overlay stays aligned
     even at >860px where the desktop base is width:100%. */
  .rg-quad-wrap { width: fit-content; max-width: 100%; margin: 0 auto; }
  .rg-quad-img { width: auto; max-width: 100%; height: auto; max-height: 62vh; max-height: 62dvh; }
  .rg-visualize-preview .rg-quad-img { animation-name: none; } /* scroll-collapse is a portrait pattern */
  /* Compare mode has no .rg-quad-img — cap the in-page slider via WIDTH (height = width / aspect),
     because cover-cropping a comparison would lie about it. --rg-photo-ar is set by both editors. */
  .rg-preview-media [data-rcs="root"] { max-width: calc(62vh * var(--rg-photo-ar, 1.5)); max-width: calc(62dvh * var(--rg-photo-ar, 1.5)); margin: 0 auto; }
  /* One scroll context on touch, whatever the width — the desktop 360px inner scroller is for mice. */
  .rg-section-body { max-height: none; overflow-y: visible; }
  /* Fullscreen editor: slim the chrome, hand the height to the photo (hint hidden; Done keeps
     clear of the photo's bottom edge). */
  .rg-fs-hint { display: none; }
  .rg-fs-editor .rg-quad-img { max-height: calc(100vh - 104px); max-height: calc(100dvh - 104px); }
}

/* ≤860px landscape: TWO-PANE master-detail — the landscape-native grammar. Left pane: the preview
   card, content-width and STICKY (photo at 52dvh, toggle above, a button-sized photoreal CTA
   below). Right pane: the whole flattened stream (accordion, price, doors, geometry) scrolling
   with the page, immediately visible beside the photo — the browse-styles/watch-the-photo loop
   that portrait gets from the pinned card, rebuilt for the wide axis. The preview spans a tall
   row range in column 1 so sticky rides the full page; every other card auto-flows down column 2
   (their portrait order values still sequence them). */
@media (max-width: 860px) and (max-height: 500px) {
  .rg-visualize-grid { grid-template-columns: auto minmax(0, 1fr); column-gap: 16px; align-items: start; }
  .rg-visualize-preview {
    grid-column: 1;
    grid-row: 1 / span 30;
    position: sticky;
    top: 8px;
    width: fit-content;
    max-width: 46vw;
  }
  .rg-visualize-col > :not(.rg-visualize-preview) { grid-column: 2; min-width: 0; }
  /* The mode toggle floats over the photo (top-center — clear of the fullscreen button top-left,
     the magnifier top-right, and the compare labels in the corners), reclaiming its row so a
     60dvh photo AND the CTA fit above the fixed quote bar: 8 + 10 + 225 + 12 + 48 + 10 = 313
     ≤ the bar's top at ~315 on a 375px viewport. The in-row fit hint is hidden — the corners are
     self-evident at this size and the fullscreen editor carries the full instruction. */
  .rg-mode-row { position: absolute; top: 16px; left: 50%; transform: translateX(-50%); width: max-content; z-index: 6; margin: 0; }
  .rg-mode-row > :not(:first-child) { display: none; }
  .rg-quad-img { max-height: 60vh; max-height: 60dvh; }
}
`;

/** Tenant-scoped layout: fetches branding once, themes the subtree via CSS variables, and
 *  provides branding + palette to descendant pages. */
export function TenantLayout() {
  const tenantSlug = useTenantSlug();
  const catalogueMode = useCatalogueRouteMode();
  const [branding, setBranding] = useState<Branding | null>(null);
  // "loading" → branding request in flight; "notfound" → the dealer slug/subdomain is unknown (404) so we
  // block the flow with a friendly message instead of rendering a working-looking generic visualizer; "ok"
  // covers both success and a transient error (keep the app usable with default branding on a blip).
  const [status, setStatus] = useState<"loading" | "ok" | "notfound">("loading");

  useEffect(() => {
    let cancelled = false;
    setBranding(null);
    setStatus("loading");
    api
      .getBranding(tenantSlug, { mode: catalogueMode })
      .then((b) => { if (!cancelled) { setBranding(b); setStatus("ok"); } })
      .catch((err) => {
        if (cancelled) return;
        const code = (err as { response?: { status?: number } })?.response?.status;
        setStatus(code === 404 ? "notfound" : "ok");
      });
    return () => { cancelled = true; };
  }, [tenantSlug, catalogueMode]);

  // Preload in neutral grey (not the green default) while the branding request is in flight, so a tenant's
  // real colour fades in cleanly on first load instead of flashing green → brand. Once loaded, an unbranded
  // tenant / transient error still falls back to the RenoGlance green default.
  const palette = useMemo(
    () => deriveBrandPalette(status === "loading" ? NEUTRAL_BRAND : branding?.brandPrimaryColor),
    [status, branding?.brandPrimaryColor],
  );

  return (
    <BrandingProvider value={{ branding, palette }}>
      <style>{GLOBAL_CSS}</style>
      <div
        style={{
          ...brandCssVars(palette),
          minHeight: "100vh",
          background: "var(--bg)",
          color: "var(--text)",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {status === "notfound" ? <DealerNotFound /> : <Outlet />}
      </div>
    </BrandingProvider>
  );
}

/** Shown when the dealer slug/subdomain in the URL doesn't resolve — a mistyped or decommissioned link.
 *  Blocks the (otherwise fully functional) visualizer so a homeowner doesn't invest effort on a dead link. */
function DealerNotFound() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div
        role="alert"
        data-testid="dealer-not-found"
        style={{
          maxWidth: 440, textAlign: "center", background: "var(--surface)", borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border)", boxShadow: "var(--shadow-md)", padding: "32px 28px",
        }}
      >
        <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 12 }} aria-hidden="true">🔍</div>
        <h1 style={{ margin: "0 0 8px", fontSize: 20 }}>We couldn't find this dealer</h1>
        <p style={{ margin: 0, color: "var(--muted)", fontSize: 14, lineHeight: 1.5 }}>
          This visualizer link may be mistyped or no longer active. Please double-check the link the company
          sent you, or contact them directly.
        </p>
      </div>
    </div>
  );
}
